import { markHostedBootWait } from '#~/routes/root/boot-budget.js';
import { ElectronAPI } from '#~/electron/index.js';
import { isElectronApp } from '#~/electron/utils.js';
import { OfflineError } from '#~/error/index.js';
import { BootErrorScreen } from '../routes/boot-overlay/boot-error-screen.jsx';
import { ElectronLogin } from '../routes/electron/login.jsx';
import { withAuthenticationRequired } from '@auth0/auth0-react';
import { Loader } from '@studio/design-system/loader';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { useAuth0 } from './index.js';
import { pollForUserInfo } from './poll-for-user-info.js';
import { authStore, useAuthStore } from './store.js';
// Spec 4 (D1): the first IPC and the conditional poll are the earliest
// boot-blocking waits in a protected document, and review-4 §2 (claim 24) found
// the `pending` / legacy-undefined branch still polls for up to 30s. Measure
// them; never record auth data — only a status string and a data-present flag.
import { beginRendererOperation, markRendererMilestone } from '#~/boot/renderer-startup.js';

// Re-exported so the existing `protect.test.ts` tests (which pre-date the
// split into `./poll-for-user-info.js`) keep working without modification.
// Per rule: stay-in-scope (ai.knowledge/global) — don't churn unrelated tests.
export { pollForUserInfo };

// Total time to poll getUserInfo IPC before giving up
const AUTH_POLL_TIMEOUT_MS = 30_000;
// Interval between getUserInfo IPC poll attempts
const AUTH_POLL_INTERVAL_MS = 2_000;

const Redirect = (): ReactElement => {
  useEffect(() => markHostedBootWait(), []);
  return <Loader />;
};

/**
 * @internal `ElectronProtect`
 *
 * @description A component that protects the electron app from unauthorized access.
 */
function ElectronProtect(): ReactElement {
  const { isAuthenticated, loading } = useElectronAuth();

  // Hard-offline at startup (no network interface). <Protect> mounts ABOVE
  // <BootOverlay> in the route tree, so throwing OfflineError here unmounts
  // the subtree before the overlay ever renders — the user lands on the
  // error-boundary <Bulletin>, a dead end with no retry. Render the unified
  // BootErrorScreen directly instead so the hard-offline case gets the same
  // "Try Again" affordance as the local-network-no-internet (boot.error) path.
  // retryFn re-checks connectivity by re-running the boot sequence (W3 owns
  // hardening what retry does).
  if (!navigator.onLine) {
    return (
      <BootErrorScreen
        source='auth-guard'
        error={{
          message: 'Unable to connect. Please check your internet connection.',
          severity: 'retryable',
          retryFn: () => window.location.reload()
        }}
      />
    );
  }

  if (loading) {
    return <Loader />;
  }

  if (!isAuthenticated) {
    return <ElectronLogin />;
  }

  return (
    <>
      <Outlet />
    </>
  );
}

/** `<Protect>` component. */
export const Protect = (): ReactElement => {
  const sessionEnded = useAuthStore((state) => state.sessionEnded);
  const { isAuthenticated } = useAuth0();
  const location = useLocation();

  const Wrapper = useMemo(
    () =>
      isElectronApp()
        ? () => null
        : withAuthenticationRequired(Outlet, {
            onRedirecting: Redirect,
            returnTo: `${location.pathname}${location.search}`
          }),
    [location]
  );

  // Keep the mounted web studio behind the modal while the SDK still owns
  // its identity. A reloaded, signed-out document must stay behind the guard.
  return sessionEnded?.phase === 'prompt' && (isElectronApp() || !isAuthenticated) ? (
    <></>
  ) : isElectronApp() ? (
    <ElectronProtect />
  ) : (
    <Wrapper />
  );
};

/**
 * Pure async helper that resolves Electron auth. Extracted from the hook so it
 * can be unit-tested without a React runtime (the frontend package has no
 * `renderHook` dependency — see `packages/react-utils/src/hooks/use-resize-observer/hook.test.ts`
 * for the ambient test-infrastructure gap).
 *
 * Contract (from plan W2):
 * - `authenticated` (data present): writes authStore, skips polling.
 * - `unauthenticated`: skips polling entirely so the login page paints in
 *   the next frame instead of waiting on the 30s poll deadline.
 *   See knowledge: pollForUserInfo-fresh-install-delay (ai.knowledge/studio.client).
 * - `pending` OR `undefined` (legacy / rolling deploy): falls through to
 *   `pollForUserInfo` to preserve the Windows webContents.send fallback
 *   documented in `electron-auth-bootstrap-race.md` (ai.knowledge/platform).
 *
 * Returns `OfflineError` when the IPC surfaces a network failure so the
 * caller can throw into an error boundary.
 * @internal Exported for testing.
 */
export const resolveElectronAuth = async (): Promise<{ offline?: OfflineError }> => {
  const closeUserInfo = beginRendererOperation('auth.getUserInfo', 'auth');
  try {
    const { data: info, status } = await ElectronAPI.getUserInfo();
    closeUserInfo('ok');
    // Booleans and a status string only — never the token or the user record.
    markRendererMilestone(
      `auth.user-info.status=${status ?? 'undefined'}.data=${info ? 'present' : 'absent'}`
    );

    if (info) {
      // Authenticated fast-path: main process already resolved userInfo.
      // No need to inspect `status` — data presence is terminal.
      authStore.setState({ userInfo: info });
      return {};
    }

    // Per plan W2: when main reports unauthenticated (empty store, no
    // tokens), skip pollForUserInfo entirely so <ElectronLogin /> renders
    // in the next paint instead of waiting on the 30s poll deadline.
    if (status === 'unauthenticated') {
      return {};
    }

    // status === 'pending' OR status === undefined (legacy / rolling deploy)
    // → preserve the pre-W2 polling behavior. The Windows IPC race fix in
    // `electron-auth-bootstrap-race.md` depends on pollForUserInfo running
    // when tokens exist but userInfo has not yet been pushed, because
    // webContents.send('login') can be silently dropped on Windows.
    const closePoll = beginRendererOperation('auth.pollForUserInfo', 'auth');
    // pollForUserInfo RESOLVES false on 30s exhaustion — it never rejects — so a
    // blanket 'ok' in a finally would label the worst case (a full 30s wait that
    // produced nothing) as a successful wait, and the diagnostics would show a
    // long healthy hop instead of the exhaustion it was. The rejection path still
    // needs its own close, hence the catch arm rather than another finally.
    const arrived = await pollForUserInfo(AUTH_POLL_TIMEOUT_MS, AUTH_POLL_INTERVAL_MS).then(
      (result) => {
        closePoll(result ? 'ok' : 'error');
        return result;
      },
      (error: unknown) => {
        closePoll('error');
        throw error;
      }
    );

    if (!arrived) {
      console.warn('[ElectronProtect] Auth poll exhausted — no userInfo received');
    }
    return {};
  } catch (err) {
    // The span is closed here for the rejection path; the closer is idempotent,
    // so a failure AFTER getUserInfo resolved does not re-close it.
    closeUserInfo('error');
    if (err instanceof OfflineError) {
      return { offline: err };
    }
    return {};
  }
};

/**
 * @description A hook that checks if the user is authenticated in the electron app.
 * Waits for BOTH getUserInfo() IPC response AND authStore.userInfo to be populated
 * before reporting loading=false, preventing a flash of unauthenticated UI.
 */
export const useElectronAuth = () => {
  const { isAuthenticated } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const ipcFetchedRef = useRef(false);

  useEffect(() => {
    // Prevent duplicate fetches on strict-mode double-mount
    if (ipcFetchedRef.current) return;
    ipcFetchedRef.current = true;

    setLoading(true);
    setError(null);

    void (async () => {
      const { offline } = await resolveElectronAuth();
      if (offline) {
        setError(offline);
      }
      setLoading(false);
    })();
  }, []);

  // Throw error to trigger error boundary
  if (error) {
    throw error;
  }

  return {
    isAuthenticated,
    loading
  };
};
